import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import styles from "./ResetNewPassword.module.css";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const ResetNewPassword = () => {
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const email = queryParams.get("email");
  const redirect = queryParams.get("redirect");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);

  // Check if we have the auth token from localStorage (user is logged in)
  useEffect(() => {
    const authToken = localStorage.getItem("token");
    if (!authToken) {
      setMessage("You need to be logged in to change your password.");
      setIsTokenValid(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (newPassword !== confirmPassword) {
      setMessage("New passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      setMessage("Password must be at least 8 characters long.");
      return;
    }

    if (oldPassword === newPassword) {
      setMessage("New password must be different from old password.");
      return;
    }

    setIsSubmitting(true);
    setMessage("");

    try {
      // Get auth token from localStorage (set during login)
      const authToken = localStorage.getItem("token");
      
      if (!authToken) {
        setMessage("Authentication token not found. Please log in again.");
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(
        `https://auth2.${backend_api_base_url}/reset_new_password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": authToken,
          },
          body: JSON.stringify({ 
            old_password: oldPassword,
            new_password: newPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Update the token in localStorage if a new one is returned
        if (data.token) {
          localStorage.setItem("token", data.token);
          console.log("New token stored successfully");
        }
        
        setMessage(data.message || "Password updated successfully! You can now log in with your new password.");
        
        // Clear form
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        
        // Optional: Redirect after 2 seconds
        setTimeout(() => {
          if (redirect === "true") {
            window.location.href = "/login";
          } else {
            // Optionally force a page reload to ensure new token is used everywhere
            window.location.href = "/my_profile";
            // Or use history.push if you don't need a full reload
            // history.push("/profile");
          }
        }, 2000);
      } else if (response.status === 401) {
        setMessage("Session expired. Please log in again.");
        localStorage.removeItem("token");
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        setMessage(data.message || data.error || "Failed to reset password.");
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle logout and redirect to login
  const handleGoToLogin = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  if (!isTokenValid) {
    return (
      <div className={styles.resetNewPasswordContainer}>
        <h1 className={styles.title}>Reset Password</h1>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{message}</p>
          <button onClick={handleGoToLogin} className={styles.submitButton}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.resetNewPasswordContainer}>
      <h1 className={styles.title}>Change Password</h1>
      <p className={styles.subtitle}>
        Enter your current password and choose a new password to update your account.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.inputGroup}>
          <label htmlFor="oldPassword" className={styles.label}>
            Current Password
          </label>
          <input
            type="password"
            id="oldPassword"
            className={styles.input}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            placeholder="Enter your current password"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="newPassword" className={styles.label}>
            New Password
          </label>
          <input
            type="password"
            id="newPassword"
            className={styles.input}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password (min. 8 characters)"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="confirmPassword" className={styles.label}>
            Confirm New Password
          </label>
          <input
            type="password"
            id="confirmPassword"
            className={styles.input}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your new password"
            required
            disabled={isSubmitting}
          />
        </div>

        <button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Updating Password..." : "Update Password"}
        </button>
      </form>

      {message && (
        <p className={message.includes("successfully") || message.includes("updated") ? styles.successMessage : styles.errorMessage}>
          {message}
        </p>
      )}
    </div>
  );
};

export default ResetNewPassword;